/** Web Push helper — service worker registration + VAPID subscription flow. */
import { api } from "@/lib/api";

/** Returns true when the browser supports the Web Push API. */
export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Detect standalone iOS install (Safari home-screen app). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

export function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  return iOS && safari;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Register the SW (idempotent). Returns the registration or null. */
export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("SW registration failed", e);
    return null;
  }
}

/** Ask user for notification permission (must be triggered by a user gesture). */
export async function askPermission() {
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const p = await Notification.requestPermission();
  return p;
}

/** Subscribe (or reuse an existing subscription) and send it to the backend. */
export async function subscribePush() {
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error("Service worker indisponible");
  const perm = await askPermission();
  if (perm !== "granted") throw new Error("Autorisation refusée");
  const { data } = await api.get("/push/vapid-public-key");
  const pub = data.public_key;
  if (!pub) throw new Error("Clé VAPID non configurée côté serveur");
  const existing = await reg.pushManager.getSubscription();
  const sub = existing || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(pub),
  });
  const j = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: j.endpoint,
    keys: j.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

/** Unsubscribe from push (client + backend). */
export async function unsubscribePush() {
  const reg = await ensureServiceWorker();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  try {
    await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } });
  } catch (e) { /* ignore */ }
  return sub.unsubscribe();
}

export async function getCurrentEndpoint() {
  const reg = await ensureServiceWorker();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub ? sub.endpoint : null;
}

export async function sendTestPush() {
  const endpoint = await getCurrentEndpoint();
  if (!endpoint) throw new Error("Aucun abonnement actif");
  const r = await api.post("/push/test", null, { params: { endpoint } });
  return r.data.ok;
}
